import { useQueueStore } from "@/stores/queue-store";
import { Capacitor } from '@capacitor/core';

// Define types for WebSocket handlers
type MessageHandlerType = (event: MessageEvent) => void;
type EventHandlerType = (event: Event) => void;

// WebSocket connection singleton
class WebSocketService {
  private static instance: WebSocketService;
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private readonly messageHandlers = new Map<string, MessageHandlerType[]>();
  private readonly eventHandlers = new Map<string, EventHandlerType[]>();
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private readonly reconnectDelay = 2000;
  private readonly autoReconnect = true;
  private connectedCallback: (() => void) | null = null;
  private disconnectedCallback: (() => void) | null = null;
  private authSent = false;

  private constructor() {}

  // Get singleton instance
  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  // Initialize connection with authentication token
  public initialize(token: string): void {
    this.token = token;
    this.authSent = false;
    this.connect();
  }

  // Get the current WebSocket instance
  public getConnection(): WebSocket | null {
    return this.ws;
  }
  
  // Close the current connection
  public close(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
  
  // Send a message through the WebSocket
  public send(data: any): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
      return true;
    }
    console.error("[WebSocket] Cannot send message, connection not open");
    return false;
  }

  // Helper method to send authentication
  private sendAuth(): boolean {
    if (!this.token || this.authSent) return false;
    
    const success = this.send({
      type: "MESSAGING_AUTH",
      token: this.token
    });
    
    if (success) {
      this.authSent = true;
    }
    
    return success;
  }

  // Request online status for a list of users
  public requestOnlineStatus(userIds: number[]): void {
    if (!this.token || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error("[WebSocket] Cannot request online status - not connected or authenticated");
      return;
    }

    this.send({
      type: "GET_ONLINE_STATUS",
      userIds,
      token: this.token
    });
  }

  // Check queue status method
  public checkQueueStatus(): void {
    if (!this.token || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error("[WebSocket] Cannot check queue status - not connected or authenticated");
      return;
    }

    this.send({
      type: "CHECK_QUEUE_STATUS",
      token: this.token
    });
  }

  // Join queue method
  public joinQueue(): void {
    if (!this.token || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error("[WebSocket] Cannot join queue - not connected or authenticated");
      return;
    }

    this.send({
      type: "JOIN_RANKED_QUEUE",
      token: this.token
    });
  }

  // Join quick match queue method
  public joinQuickMatch(): void {
    if (!this.token || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error("[WebSocket] Cannot join quick match - not connected or authenticated");
      return;
    }

    this.send({
      type: "JOIN_QUICK_QUEUE",
      token: this.token
    });
  }

  // Leave queue method
  public leaveQueue(): void {
    if (!this.token || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error("[WebSocket] Cannot leave queue - not connected or authenticated");
      return;
    }

    this.send({
      type: "LEAVE_QUEUE",
      token: this.token
    });
  }
  
  // Helper method to dispatch WebSocket messages to window event listeners
  private dispatchMessageToWindow(event: MessageEvent): void {
    window.dispatchEvent(
      new MessageEvent("message", {
        data: {
          type: "websocket_message",
          messageEvent: event
        }
      })
    );
  }
  
  // Default message handler
  private handleMessage(event: MessageEvent): void {
    // console.log("[WebSocket] Message received:", event.data);
    this.dispatchMessageToWindow(event);
    
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (error) {
      console.error("[WebSocket] Failed to parse message:", error);
      return;
    }

    this.handleMessageByType(data);
  }

  // Split message handling by type to reduce complexity
  private handleMessageByType(data: any): void {
    switch (data.type) {
      case "ONLINE_STATUS":
        this.handleOnlineStatus(data);
        break;
      case "USER_ONLINE":
      case "USER_OFFLINE":
        this.handleUserStatusChange(data);
        break;
      case "QUEUE_JOIN_SUCCESS":
      case "QUEUE_STATUS_RESPONSE":
      case "QUEUE_LEAVE_SUCCESS":
        this.handleQueueStatus(data);
        break;
      case "MATCH_FOUND":
        this.handleMatchFound(data);
        break;
      case "MATCH_ACCEPTANCE_UPDATE":
        this.handleMatchAcceptanceUpdate(data);
        break;
      case "MATCH_REJECTED":
        this.handleMatchRejected(data);
        break;
      case "GAME_CREATED":
        this.handleGameCreated(data);
        break;
      case "MESSAGING_AUTH_SUCCESS":
        this.handleAuthSuccess(data);
        break;
    }

    // Dispatch to registered handlers with proper message event creation
    const handlers = this.messageHandlers.get(data.type);
    if (handlers) {
      handlers.forEach(handler => {
        const messageEvent = new MessageEvent('message', { 
          data: JSON.stringify(data)  // Create new message event with stringified data
        });
        handler(messageEvent);
      });
    }
  }

  private handleOnlineStatus(data: { onlineUsers: number[]; timestamp: number }): void {
    window.dispatchEvent(new CustomEvent('friend_online_status', {
      detail: {
        onlineUsers: data.onlineUsers,
        timestamp: data.timestamp
      }
    }));
  }

  private handleUserStatusChange(data: { userId: number; type: 'USER_ONLINE' | 'USER_OFFLINE' }): void {
    window.dispatchEvent(new CustomEvent('friend_status_change', {
      detail: {
        userId: data.userId,
        isOnline: data.type === 'USER_ONLINE'
      }
    }));
  }

  private handleQueueStatus(data: { type: string; inQueue?: boolean }): void {
    const store = useQueueStore.getState();

    switch (data.type) {
      case "QUEUE_JOIN_SUCCESS":
        store.setIsInQueue(true);
        store.setIsSearching(true);
        break;
      case "QUEUE_STATUS_RESPONSE":
        store.setIsInQueue(data.inQueue === true);
        store.setIsSearching(data.inQueue === true);
        break;
      case "QUEUE_LEAVE_SUCCESS":
        store.setIsInQueue(false);
        store.setIsSearching(false);
        break;
    }
  }

  private handleMatchFound(data: any): void {
    const store = useQueueStore.getState();
    store.setMatchToken(data.token);
    store.setMatchOpponent(data.opponent?.username ?? "Opponent");
    store.setTimeRemaining(30);
    store.setAcceptedMatch(false);
    store.setShowMatchDialog(true);
    store.setIsInQueue(false);
    store.setIsSearching(false);
  }

  private handleMatchAcceptanceUpdate(data: any): void {
    // console.log("[WebSocket] Match acceptance update:", data);
  }

  private handleMatchRejected(data: any): void {
    const store = useQueueStore.getState();
    const currentMatchToken = store.matchToken;
    
    if (!currentMatchToken || (data.token && currentMatchToken !== data.token)) {
      // console.log("[WebSocket] Ignoring match rejection for different match");
      return;
    }

    const userId = this.extractUserIdFromToken();
    const rejectionMessage = data.rejectedBy === userId
      ? "You rejected the match"
      : "Other player rejected the match.";

    window.dispatchEvent(new CustomEvent('game-notification', { 
      detail: { 
        title: "Match Rejected",
        message: rejectionMessage,
        type: "error",
        timeout: 5000
      } 
    }));

    if (data.rejectedBy !== userId) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('game-notification', { 
          detail: { 
            title: "Want to try again?",
            message: "Click here to search for another match",
            type: "info",
            timeout: 10000
          } 
        }));
      }, 1000);
    }

    // Reset match states
    store.setShowMatchDialog(false);
    store.setAcceptedMatch(false);
    store.setIsInQueue(false);
    store.setIsSearching(false);
    store.setMatchToken(null);
  }

  private handleGameCreated(data: { gameId?: number }): void {
    if (!data.gameId) {
      console.error("[WebSocket] Game created but no gameId provided");
      return;
    }

    const store = useQueueStore.getState();
    store.setShowMatchDialog(false);
    store.setAcceptedMatch(false);
    store.setIsInQueue(false);
    store.setIsSearching(false);
    store.setMatchToken(null);

    this.dispatchGameCreatedEvent(data.gameId);
  }

  private handleAuthSuccess(data: { userId: number }): void {
    // console.log("[WebSocket] Messaging authenticated successfully");
  }

  private dispatchGameCreatedEvent(gameId: number): void {
    setTimeout(() => {
      try {
        window.dispatchEvent(new CustomEvent("game_created", { 
          detail: { gameId } 
        }));
        window.location.href = `/game/${gameId}`;
      } catch (error) {
        console.error("[WebSocket] Error dispatching game_created event:", error);
      }
    }, 100);
  }

  private extractUserIdFromToken(): number | null {
    try {
      if (!this.token) return null;
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      return payload.userId;
    } catch (error) {
      console.error("[WebSocket] Error extracting userId from token:", error);
      return null;
    }
  }
  
  // Connection opened
  private handleOpen(event: Event): void {
    // console.log("[WebSocket] Connection opened");
    this.reconnectAttempts = 0;

    // Send authentication immediately when connection opens
    if (!this.sendAuth()) {
      console.error("[WebSocket] Failed to send authentication");
      this.close();
      return;
    }

    if (this.connectedCallback) {
      this.connectedCallback();
    }

    const handlers = this.eventHandlers.get('open');
    if (handlers) {
      handlers.forEach(handler => handler(event));
    }
  }

  // Connection closed
  private handleClose(event: CloseEvent): void {
    // console.log("[WebSocket] Connection closed");
    this.ws = null;
    this.authSent = false;

    if (this.disconnectedCallback) {
      this.disconnectedCallback();
    }

    if (this.autoReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      // console.log(`[WebSocket] Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      setTimeout(() => this.connect(), delay);
    }

    const handlers = this.eventHandlers.get('close');
    if (handlers) {
      handlers.forEach(handler => handler(event));
    }
  }

  // Connection error
  private handleError(event: Event): void {
    console.error("[WebSocket] Connection error:", event);

    const handlers = this.eventHandlers.get('error');
    if (handlers) {
      handlers.forEach(handler => handler(event));
    }
  }

  // Add message handler
  public addMessageHandler(type: string, handler: MessageHandlerType): void {
    const handlers = this.messageHandlers.get(type) || [];
    handlers.push(handler);
    this.messageHandlers.set(type, handlers);
  }

  // Remove message handler
  public removeMessageHandler(type: string, handler: MessageHandlerType): void {
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index !== -1) {
        handlers.splice(index, 1);
        if (handlers.length === 0) {
          this.messageHandlers.delete(type);
        } else {
          this.messageHandlers.set(type, handlers);
        }
      }
    }
  }

  // Add event handler
  public addEventHandler(type: string, handler: EventHandlerType): void {
    const handlers = this.eventHandlers.get(type) || [];
    handlers.push(handler);
    this.eventHandlers.set(type, handlers);
  }

  // Remove event handler
  public removeEventHandler(type: string, handler: EventHandlerType): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index !== -1) {
        handlers.splice(index, 1);
        if (handlers.length === 0) {
          this.eventHandlers.delete(type);
        } else {
          this.eventHandlers.set(type, handlers);
        }
      }
    }
  }

  // Set callbacks for connection events
  public setCallbacks(connected: () => void, disconnected: () => void): void {
    this.connectedCallback = connected;
    this.disconnectedCallback = disconnected;
  }

  // Get transformed URL for specific environments (e.g., Capacitor)
  private getWebSocketUrl(): string {
    if (Capacitor.isNativePlatform()) {
      const serverUrl = (import.meta.env.VITE_SERVER_URL as string) ?? window.location.origin;
      const parsedUrl = new URL(serverUrl);
      const wsUrl = `${parsedUrl.protocol === 'https:' ? 'wss:' : 'ws:'}//${parsedUrl.host}/ws`;
      // console.log('[WebSocketService] Mobile WebSocket - using absolute URL:', wsUrl);
      return wsUrl;
    } else {
      const wsUrl = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/ws`;
      // console.log('[WebSocketService] Web WebSocket - using relative URL:', wsUrl);
      return wsUrl;
    }
  }

  // Connect to WebSocket server
  private connect(): void {
    if (this.ws) {
      console.warn("[WebSocket] Connection already exists");
      return;
    }

    const url = this.getWebSocketUrl();
    // console.log("[WebSocket] Connecting to:", url);

    try {
      this.ws = new WebSocket(url);
      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
    } catch (error) {
      console.error("[WebSocket] Failed to create connection:", error);
      this.ws = null;
    }
  }
}

export default WebSocketService;